import { useCreationStore } from '@/store/useCreationStore';
import { isSpecialProjectType } from './projectUtils';
import { captureUtmParams } from './utm';
import { buildRuntimeHashUrl } from './appUrl';

/**
 * 获取注入了全局参数的安全路径
 * @param to 目标路径
 * @returns 拼接了 projectType 等参数后的路径
 */
export function getSafePath(to: string): string {
  const projectTypeId = useCreationStore.getState().projectTypeId;

  if (isSpecialProjectType(projectTypeId)) {
    const hasParam = to.includes('projectTypeId=');
    if (!hasParam) {
      const separator = to.includes('?') ? '&' : '?';
      // 如果是特殊模式，强制带上参数（如果是 0 则传 0，否则传实际 ID）
      return `${to}${separator}projectTypeId=${projectTypeId}`;
    }
  }

  return to;
}

/**
 * 初始化 URL 参数处理逻辑
 * 包含 OAuth 回调引导、ProjectTypeId 路由引导以及全局参数同步
 */
export function initUrlParamsHandling() {
  // 提取参数的辅助函数，同时支持 search 和 hash
  const getParam = (name: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashPart = window.location.hash.split('?')[1];
    const hashParams = new URLSearchParams(hashPart);
    return hashParams.get(name) || searchParams.get(name);
  };

  const _oauthCode = getParam('code');
  const _oauthState = getParam('state');
  const _projectTypeId = getParam('projectTypeId');

  // 1. 处理 OAuth 回调引导 (必须在根路径处理)
  const shouldRouteToOauthCallback =
    Boolean(_oauthCode) &&
    Boolean(_oauthState) &&
    !window.location.hash.includes('/oauth/callback');

  if (shouldRouteToOauthCallback) {
    const searchStr =
      window.location.search ||
      (window.location.hash.includes('?') ? `?${window.location.hash.split('?')[1]}` : '');
    window.location.replace(buildRuntimeHashUrl(`/oauth/callback${searchStr}`));
    return;
  }

  // 2. 处理 _projectTypeId=999 引导 (可选，用于规整 URL)
  const defaultTypeId = String(import.meta.env.VITE_SSH_PROJECT_TYPE_ID);
  if (
    (_projectTypeId === defaultTypeId || _projectTypeId === '0') &&
    (!window.location.hash || window.location.hash === '#/')
  ) {
    const searchStr =
      window.location.search ||
      (window.location.hash.includes('?') ? `?${window.location.hash.split('?')[1]}` : '');
    window.location.replace(buildRuntimeHashUrl(`/create${searchStr}`));
    return;
  }

  // 3. 定义同步函数
  const syncProjectType = () => {
    let projectTypeId = getParam('projectTypeId');
    if (projectTypeId === null) {
      projectTypeId = import.meta.env.VITE_SSH_PROJECT_TYPE_ID;
    }

    useCreationStore.getState().setProjectTypeId(Number(projectTypeId));
  };

  syncProjectType();

  // 4. 捕获并持久化 UTM 参数
  captureUtmParams();
}
